import { addRobotoFont } from '../../../utils/fonts/robotoFont'
import { inlineComputedStyles } from './svg-inline-styles'

export class PdfFontError extends Error { constructor() { super('font'); this.name = 'PdfFontError' } }
export class PdfPageError extends Error {
  page: number
  constructor(page: number, message: string) { super(message); this.name = 'PdfPageError'; this.page = page }
}

export interface PdfExportArgs {
  pageW: number
  pageH: number
  orientation: 'portrait' | 'landscape'
  total: number
  fileName: string
  /** Mounts page `index` into `host`; returns the unmount function. */
  renderPage: (index: number, host: HTMLElement) => () => void
  onProgress: (done: number, total: number) => void
  signal: { cancelled: boolean }
}

const yieldToUi = () => new Promise<void>((resolve) => setTimeout(resolve, 0))

/** Pure — no DOM access, safe to import and unit-test under vitest's node environment. */
export const pdfFileName = (scheduleName: string, todayIso: string): string =>
  `${scheduleName.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) || 'schedule'}-${todayIso}.pdf`

/**
 * Spec §6: one hidden host, one page at a time, computed styles inlined, svg2pdf per page, Roboto
 * embedded. `jspdf` and `svg2pdf.js` are imported dynamically here (not at module top level) so that
 * importing this module in a non-DOM environment (vitest, node) never touches `document`/`window`.
 */
export const exportPagesToPdf = async ({
  pageW,
  pageH,
  orientation,
  total,
  fileName,
  renderPage,
  onProgress,
  signal,
}: PdfExportArgs): Promise<void> => {
  const [{ jsPDF }, svgMod] = await Promise.all([import('jspdf'), import('svg2pdf.js')])
  const doc = new jsPDF({ unit: 'px', format: [pageW, pageH], orientation, hotfixes: ['px_scaling'], compress: true })
  if (!(await addRobotoFont(doc))) throw new PdfFontError()

  const host = document.createElement('div')
  host.setAttribute('aria-hidden', 'true')
  // Off-screen but rendered — computed styles need real layout — no inline style needed for a fixed offset.
  host.className = 'schedule-print fixed -left-[10000px] top-0'
  document.body.appendChild(host)

  // svg2pdf measures every `<text>` against the face the host actually resolved. Roboto is a web
  // font: with the first page rendered before it loads, the fallback's metrics decide the layout.
  await (document.fonts && document.fonts.ready ? document.fonts.ready.catch(() => undefined) : Promise.resolve())

  try {
    for (let i = 0; i < total; i++) {
      if (signal.cancelled) return
      let unmount: (() => void) | undefined
      try {
        unmount = renderPage(i, host)
        const svg = host.querySelector('svg.print-page') as SVGSVGElement | null
        if (!svg) throw new Error('page did not render')
        inlineComputedStyles(svg, 'Roboto')
        if (i > 0) doc.addPage([pageW, pageH], orientation)
        await svgMod.svg2pdf(svg, doc, { x: 0, y: 0, width: pageW, height: pageH })
      } catch (e: any) {
        throw new PdfPageError(i + 1, e?.message || String(e))
      } finally {
        unmount?.()
      }
      onProgress(i + 1, total)
      await yieldToUi()
    }
    if (!signal.cancelled) doc.save(fileName)
  } finally {
    host.remove()
  }
}
